import { useState } from "react";
import Button from "../../../../components/fundamentals/button";
import Modal from "../../../../components/fundamentals/modal";
import { Product } from "../../../../models/Product";
import ProductTable, { columnProduct } from "../../products/products-page/product-table/ProductTable";
import BrandPicker from "../../brand-mappings/BrandPicker";
import CategoryPicker from "../../category-mappings/CategoryPicker";
import SupplierPicker from "./SupplierPicker";
import { BrandOption } from "../../../../services/brand-mappings.service";
import { Category } from "../../../../models/Category";
import { Supplier } from "../../../../models/Supplier";
import productsService from "../../../../services/products.service";
import useNotification from "../../../../hooks/use-notification";

interface IProps {
  open: boolean;
  onClose: () => void;
  productsSelected: Product[]
  save: (products: Product[]) => void;
}

export default function ModalProducts({
  open,
  onClose,
  productsSelected,
  save
}: IProps) {

  const [selected, setSelected] = useState<Product[]>([...productsSelected]);
  const [showBrandPicker, setShowBrandPicker] = useState<boolean>(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState<boolean>(false);
  const [showSupplierPicker, setShowSupplierPicker] = useState<boolean>(false);
  const [adding, setAdding] = useState<boolean>(false);
  const notification = useNotification();

  function handleClose() {
    setSelected(productsSelected)
    onClose()
  }

  // RAG-45/49 : ajoute d'un coup tous les produits d'un filtre (marque, catégorie
  // entière ou fournisseur entier), sans les cocher un par un.
  async function addBulk(
    filters: { brand_id?: string; category_id?: string; provider?: string },
    origin: string
  ) {
    setAdding(true);
    try {
      const products = await productsService.selectForBulk(filters);
      const existingIds = new Set(selected.map((p) => p.id));
      const toAdd = products.filter((p) => !existingIds.has(p.id));
      setSelected([...selected, ...toAdd]);
      notification(
        "Produits ajoutés",
        `${toAdd.length} produit(s) de ${origin} ajouté(s) à la sélection.`,
        "success"
      );
    } catch (e) {
      notification("Erreur", `Impossible d'ajouter les produits de ${origin}.`, "error");
    } finally {
      setAdding(false);
    }
  }

  function handleAddBrand(brand: BrandOption) {
    setShowBrandPicker(false);
    addBulk({ brand_id: brand.id }, brand.name);
  }

  function handleAddCategory(category: Category) {
    setShowCategoryPicker(false);
    addBulk({ category_id: String(category.id) }, `la catégorie ${category.name}`);
  }

  function handleAddSupplier(supplier: Supplier) {
    setShowSupplierPicker(false);
    addBulk({ provider: supplier.provider }, `le fournisseur ${supplier.provider}`);
  }

  return (
    <>
    <Modal open={open} handleClose={handleClose} isLargeModal>
      <Modal.Body>
        <Modal.Header handleClose={handleClose}>
          <div className="flex items-center justify-between w-full gap-x-base">
            <div>
              <h1 className="inter-xlarge-semibold">
                Choisir des produits
              </h1>
              <p>{selected.length} produits séléctionné(s)</p>
            </div>
            {/* RAG-45/49 : ajout en masse par marque, catégorie entière ou fournisseur entier. */}
            <div className="flex items-center gap-x-xsmall">
              <Button
                variant="secondary"
                size="small"
                type="button"
                loading={adding}
                onClick={() => setShowBrandPicker(true)}
              >
                Ajouter une marque entière
              </Button>
              <Button
                variant="secondary"
                size="small"
                type="button"
                loading={adding}
                onClick={() => setShowCategoryPicker(true)}
              >
                Ajouter une catégorie entière
              </Button>
              <Button
                variant="secondary"
                size="small"
                type="button"
                loading={adding}
                onClick={() => setShowSupplierPicker(true)}
              >
                Ajouter un fournisseur entier
              </Button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Content>
          <ProductTable
            selected={selected}
            setSelected={setSelected}
            columns = {[columnProduct.SELECT, columnProduct.NAME, columnProduct.PRICE, columnProduct.EAN, columnProduct.STOCK]}
            useElasticSearch={true}
          />
        </Modal.Content>
        <Modal.Footer>
          <div className="flex items-center justify-end w-full gap-x-xsmall">
            <Button
              variant="secondary"
              size="small"
              type="reset"
              onClick={handleClose}
            >
              Annuler
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={() => save(selected)}
              disabled={selected.length == 0}
            >
              Enregister
            </Button>
          </div>
        </Modal.Footer>
      </Modal.Body>
    </Modal>
    {showBrandPicker && (
      <BrandPicker title="Ajouter tous les produits d'une marque" onClose={() => setShowBrandPicker(false)} onPick={handleAddBrand} />
    )}
    {showCategoryPicker && (
      <CategoryPicker title="Ajouter tous les produits d'une catégorie" onClose={() => setShowCategoryPicker(false)} onPick={handleAddCategory} />
    )}
    {showSupplierPicker && (
      <SupplierPicker onClose={() => setShowSupplierPicker(false)} onPick={handleAddSupplier} />
    )}
    </>
  );
}
